'use client';

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Box, IconButton } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  Undo,
  Redo,
} from "@mui/icons-material";
import { useEffect } from "react";

export default function TipTapEditor({
  content = "",
  onChange,
  placeholder = "",
  minHeight = "100px",
  editorProps = {}
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false, // Disable link from StarterKit
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "tiptap-link",
        },
      }),
    ],
    content: "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
        style: `min-height: ${minHeight}; padding: 12px; border: 1px solid #e0e0e0; border-radius: 4px;`,
        "data-placeholder": placeholder,
        ...editorProps.attributes,
      },
      ...editorProps,
    },
  });

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        editor.commands.setContent(content || "");
      }
    }
  }, [editor, content]);

  const handleLinkClick = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "white",
      }}
    >
      {editor && (
        <Box
          sx={{
            px: 0.5,
            py: 0.25,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
            display: "flex",
            gap: 0.25,
          }}
        >
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            size="small"
            title="Bold"
            sx={{
              borderRadius: 1,
              bgcolor: editor.isActive("bold") ? "action.selected" : "transparent",
            }}
          >
            <FormatBold sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            size="small"
            title="Italic"
            sx={{
              borderRadius: 1,
              bgcolor: editor.isActive("italic") ? "action.selected" : "transparent",
            }}
          >
            <FormatItalic sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            size="small"
            title="Bullet List"
            sx={{
              borderRadius: 1,
              bgcolor: editor.isActive("bulletList") ? "action.selected" : "transparent",
            }}
          >
            <FormatListBulleted sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            size="small"
            title="Numbered List"
            sx={{
              borderRadius: 1,
              bgcolor: editor.isActive("orderedList") ? "action.selected" : "transparent",
            }}
          >
            <FormatListNumbered sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={handleLinkClick}
            size="small"
            title="Add Link"
            sx={{
              borderRadius: 1,
              bgcolor: editor.isActive("link") ? "action.selected" : "transparent",
            }}
          >
            <LinkIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            size="small"
            title="Undo"
            sx={{ borderRadius: 1 }}
          >
            <Undo sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            size="small"
            title="Redo"
            sx={{ borderRadius: 1 }}
          >
            <Redo sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
      <EditorContent editor={editor} />
    </Box>
  );
}